import React, { useState, useCallback, useRef } from 'react';
import ProductsGridMobile from './ProductsGridMobile';
import AboutSectionMobile from './HiperaliaInfoSectionmMobile';
import AboutSectionCategoriesMobile from './AboutSectionCategoriesMobile';
import FAQSectionMobileCategories from './FAQSectionMobileCategories';
import FAQSectionMobile from './FAQSectionMobile';
import NewsletterSectionMobile from './NewsletterSectionMobile';
import InfoCardsWithRecipeMobile from './InfoCardsWithRecipeMobile';
import MobileWrapper from '@/components/common/MobileWrapper';
import { Manufacturer, ProductBase } from '@/types/product';
import Pagination from '@/components/ui/Pagination';
import { PaginationInfo } from '@/types/pagination';
import ProductsFilterBarMobile from './ProductsFilterBarMobile';
import { MenuRoute, BreadcrumbMenu } from '@/types/menu';
import { CartItem } from '@/types/cart';
import { fetchProductStock } from '@/hooks/useProducts';
import { useToastStore } from '@/store/useToastStore';
import { useAddToCart } from '@/hooks/useAddToCart';

interface ProductsPageContentMobileProps {
  className?: string;
  products?: ProductBase[];
  menuCategories?: MenuRoute[];
  breadcrumbMenu?: BreadcrumbMenu;
  // Props de paginación
  paginationInfo?: PaginationInfo;
  manufacturers?: Manufacturer[];
  // Props de filtros
  sortBy: string;
  selectedOptions: string[];
  selectedManufacturer: Manufacturer | null;
  priceRange: [number, number];
  categoryId?: number; // ID de categoría para secciones específicas
  handleSortChange: (sort: string) => void;
  handleOptionsChange: (options: string) => void;
  handleMultipleOptionsChange: (options: string[]) => void;
  handleFilterChange: (manufacturer?: number, price_min?: number, price_max?: number) => void;
  handlePageChange: (page: number) => void;
}

const ProductsPageContentMobile: React.FC<ProductsPageContentMobileProps> = ({
  className = '',
  products = [],
  menuCategories = [],
  breadcrumbMenu,
  paginationInfo,
  manufacturers = [],
  sortBy,
  selectedOptions,
  selectedManufacturer,
  priceRange,
  categoryId,
  handleSortChange,
  handleOptionsChange,
  handleMultipleOptionsChange,
  handleFilterChange,
  handlePageChange,
}) => {
  // Estados para filtros básicos
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Usar productos directamente (ya vienen como ProductCard)
  const productsToUse = products;

  // Usar el store del carrito
  const { addToCart } = useAddToCart();
  const { showToast } = useToastStore();
  const handleAddToCart = async (productId: number) => {
    // Encontrar el producto por ID
    const product = productsToUse.find((p) => p.id === productId);

    const totalStock = await fetchProductStock(productId, 'es');
    if (totalStock === 0) {
      showToast('Producto sin stock', 'error');
      return;
    }

    if (product) {
      // Crear un CartItem desde el ProductBase
      const cartItem: CartItem = {
        id: product.id.toString(),
        name: product.title,
        image: product.image,
        price: product.priceFinalInc,
        tax: product.priceFinalInc - product.priceFinalExc,
        quantity: 1,
        weight: product.weight || '',
        brand: product.brand || '',
        sku: product.slug,
        barcode: product.barcode || '',
        category: product.breadcrumb?.[0]?.name || '',
        category2: product.breadcrumb?.[1]?.name || '',
        category3: product.breadcrumb?.[2]?.name || '',
        variant: 'default',
      };

      // Agregar al carrito (el toast se mostrará automáticamente desde el store)
      addToCart(cartItem);
    } else {
      console.error(`Product with id ${productId} not found`);
    }
  };

  const handleToggleFavorite = (productId: number) => {
    // Manejar favoritos aquí
  };

  // Función para manejar clics en chips de filtro
  const handleChipFilterClick = useCallback(
    (filterValue: string) => {
      const newSelectedFilters = selectedFilters.includes(filterValue)
        ? selectedFilters.filter((f) => f !== filterValue)
        : [...selectedFilters, filterValue];

      setSelectedFilters(newSelectedFilters);
      handleOptionsChange(filterValue);
    },
    [selectedFilters, handleOptionsChange]
  );

  const handleSortClick = (sortValue: string) => {
    handleSortChange(sortValue);
  };

  const toggleViewMode = () => {
    const newViewMode = viewMode === 'grid' ? 'list' : 'grid';
    setViewMode(newViewMode);
  };

  // Método para manejar la aplicación de filtros desde el modal
  const handleFilterApply = (filters: any) => {
    // Aplicar los filtros usando las funciones del componente padre
    if (filters.sort) {
      handleSortChange(filters.sort);
    }

    if (filters.priceRange) {
      handleFilterChange(filters.manufacturer?.id, filters.priceRange[0], filters.priceRange[1]);
    }

    // Manejar categorías de una vez para evitar problemas de estado
    if (filters.categories) {
      const newOptions = filters.categories;
      handleMultipleOptionsChange(newOptions);
    }

    setIsFilterOpen(false);
  };

  const finalProducts = productsToUse;

  return (
    <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:157:4" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="157" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      {/* Breadcrumb and Filter Section */}
      <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:159:6" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="159" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20pb-4%22%7D" className="px-4 pb-4">
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:160:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="160" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20justify-between%22%7D" className="flex flex-row items-center justify-between">
          {/* Breadcrumb */}
          <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:162:10" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="162" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20gap-x-2%22%7D" className="flex flex-row items-center gap-x-2">
            <img data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:163:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="163" data-component-file="ProductsPageContentMobile.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_1.svg%22%2C%22alt%22%3A%22Home%22%7D" src="/images/img_vector_gray_900_1.svg" alt="Home" width={16} height={15} />
            <img data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:164:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="164" data-component-file="ProductsPageContentMobile.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_2.svg%22%2C%22alt%22%3A%22Arrow%22%7D" src="/images/img_vector_gray_900_2.svg" alt="Arrow" width={8} height={14} />
            <span data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:165:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="165" data-component-file="ProductsPageContentMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-regular%20font-normal%20text-sm%20leading-4%20text-global-1%22%2C%22textContent%22%3A%22Productos%22%7D" className="font-tt-norms-regular font-normal text-sm leading-4 text-global-1">
              Productos
            </span>
          </div>

          {/* Filter Button */}
          <button data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:171:10" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="171" data-component-file="ProductsPageContentMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22rounded-lg%20border%20flex%20items-center%20justify-center%22%7D"
            onClick={() => {
              if (isFilterOpen) {
                setIsFilterOpen(false);
              } else {
                setIsFilterOpen(true);
                setTimeout(() => {
                  filterPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 0);
              }
            }}
            className="rounded-lg border flex items-center justify-center"
            style={{
              backgroundColor: '#FEF5E6',
              borderColor: 'rgba(61, 28, 26, 0.3)',
              width: '104px',
              height: '33px',
            }}
          >
            <span data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:190:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="190" data-component-file="ProductsPageContentMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-regular%20text-sm%20leading-4%22%7D"
              className="font-tt-norms-regular text-sm leading-4"
              style={{
                color: '#3D1C1A',
                fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif",
              }}
            >
              {isFilterOpen ? 'Cerrar filtro' : 'Filtro'}
            </span>
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:204:6" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="204" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={filterPanelRef}>
        <ProductsFilterBarMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:205:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="205" data-component-file="ProductsPageContentMobile.tsx" data-component-name="ProductsFilterBarMobile" data-component-content="%7B%22elementName%22%3A%22ProductsFilterBarMobile%22%7D"
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleFilterApply}
          manufacturers={manufacturers}
          sortBy={sortBy}
          selectedOptions={selectedOptions}
          selectedManufacturer={selectedManufacturer}
          priceRange={priceRange}
        />
      </div>

      {/* Main Content */}
      <MobileWrapper data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:218:6" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="218" data-component-file="ProductsPageContentMobile.tsx" data-component-name="MobileWrapper" data-component-content="%7B%22elementName%22%3A%22MobileWrapper%22%7D">
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:219:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="219" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-10%20rounded-%5B12px%5D%20mx-4%20mt-2%22%7D" className="bg-global-10 rounded-[12px] mx-4 mt-2">
          {/* Main Content - Grid de productos */}
          <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:221:10" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="221" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <ProductsGridMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:222:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="222" data-component-file="ProductsPageContentMobile.tsx" data-component-name="ProductsGridMobile" data-component-content="%7B%22elementName%22%3A%22ProductsGridMobile%22%7D"
              products={finalProducts}
              onAddToCart={handleAddToCart}
              onToggleFavorite={handleToggleFavorite}
            />
          </div>

          {/* Paginación */}
          {paginationInfo && (
            <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:231:12" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="231" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20mb-4%22%7D" className="mt-6 mb-4">
              <Pagination data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:232:14" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="232" data-component-file="ProductsPageContentMobile.tsx" data-component-name="Pagination" data-component-content="%7B%22elementName%22%3A%22Pagination%22%2C%22className%22%3A%22%22%7D"
                paginationInfo={paginationInfo}
                handlePageChange={handlePageChange}
                className=""
              />
            </div>
          )}
        </div>

        {/* Newsletter Section */}
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:242:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="242" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%22%7D" className="mt-4">
          <NewsletterSectionMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:243:10" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="243" data-component-file="ProductsPageContentMobile.tsx" data-component-name="NewsletterSectionMobile" data-component-content="%7B%22elementName%22%3A%22NewsletterSectionMobile%22%7D" />
        </div>

        {/* Professional Section */}
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:247:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="247" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%22%7D" className="mt-2">
          <InfoCardsWithRecipeMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:248:10" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="248" data-component-file="ProductsPageContentMobile.tsx" data-component-name="InfoCardsWithRecipeMobile" data-component-content="%7B%22elementName%22%3A%22InfoCardsWithRecipeMobile%22%7D" />
        </div>

        {/* About Section */}
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:252:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="252" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%20mb-4%22%7D" className="mt-4 mb-4">
          {(() => {
            // Usar la categoría más específica disponible (nivel 3, luego 2, luego 1)
            const categoryId =
              breadcrumbMenu?.menuLevel3?.id ||
              breadcrumbMenu?.menuLevel2?.id ||
              breadcrumbMenu?.menuLevel1?.id;
            return categoryId ? (
              <AboutSectionCategoriesMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:260:14" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="260" data-component-file="ProductsPageContentMobile.tsx" data-component-name="AboutSectionCategoriesMobile" data-component-content="%7B%22elementName%22%3A%22AboutSectionCategoriesMobile%22%7D" categoryId={categoryId} />
            ) : (
              <AboutSectionMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:262:14" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="262" data-component-file="ProductsPageContentMobile.tsx" data-component-name="AboutSectionMobile" data-component-content="%7B%22elementName%22%3A%22AboutSectionMobile%22%7D" />
            );
          })()}
        </div>

        {/* FAQ Section */}
        <div data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:268:8" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="268" data-component-file="ProductsPageContentMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          {(() => {
            // Usar la categoría más específica disponible (nivel 3, luego 2, luego 1)
            const categoryId =
              breadcrumbMenu?.menuLevel3?.id ||
              breadcrumbMenu?.menuLevel2?.id ||
              breadcrumbMenu?.menuLevel1?.id;
            return categoryId ? (
              <FAQSectionMobileCategories data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:276:14" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="276" data-component-file="ProductsPageContentMobile.tsx" data-component-name="FAQSectionMobileCategories" data-component-content="%7B%22elementName%22%3A%22FAQSectionMobileCategories%22%7D" categoryId={categoryId} />
            ) : (
              <FAQSectionMobile data-component-id="src/components/sections/mobile/ProductsPageContentMobile.tsx:278:14" data-component-path="src/components/sections/mobile/ProductsPageContentMobile.tsx" data-component-line="278" data-component-file="ProductsPageContentMobile.tsx" data-component-name="FAQSectionMobile" data-component-content="%7B%22elementName%22%3A%22FAQSectionMobile%22%7D" />
            );
          })()}
        </div>
      </MobileWrapper>
    </div>
  );
};

export default ProductsPageContentMobile;
